import React, { useContext, useRef, useState, useEffect } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import categories from '../categories';
import Card from '../components/Card';
import { food_items } from '../food';
import { dataContext } from '../UserContext';
import { IoMdCloseCircleOutline } from "react-icons/io";
import { useDispatch, useSelector } from 'react-redux';
import { ClearCart } from '../cartSlice';
import Cart from '../components/Cart';
import { toast } from 'react-toastify';
import banner from '../assets/home-header.png';

function Home() {
  let { cate, setCate, input, showCart, setShowCart } = useContext(dataContext);
  const menuRef = useRef(null);
  const dispatch = useDispatch();

  const [orderStep, setOrderStep] = useState('button');
  const [form, setForm] = useState({ name: '', phone: '', address: '' });
  const [paymentMethod, setPaymentMethod] = useState('');
  const [upiId, setUpiId] = useState('');
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '' });

  const items = useSelector(state => state.cart);
  const subtotal = items.reduce((total, item) => total + item.qty * item.price, 0);
  const deliveryFee = 20;
  const taxes = subtotal * 0.5 / 100;
  const total = Math.floor(subtotal + deliveryFee + taxes);

  useEffect(() => {
    // Scroll to cart when form begins
    if (orderStep === 'form' && showCart) {
      document.querySelector('.cart-sidebar')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [orderStep, showCart]);

  useEffect(() => {
    if (items.length > 0 && orderStep === 'success') {
      setOrderStep('button');
    }
  }, [items]);

  useEffect(() => {
    if (!showCart && orderStep !== 'success') {
      setOrderStep('button');
    }
  }, [showCart]);

  const filter = (category) => {
    if (category === "All") {
      setCate(food_items);
    } else {
      const newList = food_items.filter(item => item.food_category === category.toLowerCase());
      setCate(newList);
    }
  };

  const handleViewMenuClick = () => {
    menuRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Please fill in all details.");
      return;
    }
    setOrderStep('payment');
  };

  const handleFinalPayment = () => {
    if (paymentMethod === 'upi' && !upiId) {
      toast.error("Please enter UPI ID");
      return;
    }

    if (paymentMethod === 'card') {
      const cardNumberClean = card.number.replace(/\s/g, '');
      if (!/^\d{16}$/.test(cardNumberClean)) {
        toast.error("Enter a valid 16-digit card number");
        return;
      }
      if (!card.name || !card.expiry || !card.cvv) {
        toast.error("Please fill all card details");
        return;
      }
    }

    toast.success("Order Placed Successfully ✅", {
      position: "top-right",
      autoClose: 3000,
    });

    dispatch(ClearCart());
    setOrderStep('success');
    setForm({ name: '', phone: '', address: '' });
    setPaymentMethod('');
    setUpiId('');
    setCard({ number: '', name: '', expiry: '', cvv: '' });
  };

  return (
    <div className='bg-slate-100 w-full min-h-[100vh]'>
      <Nav />

      {!input && (
        <div className='flex flex-wrap justify-center items-center gap-6 w-[100%]'>
          {categories.map((item) => (
            <div
              key={item.name}
              className='md:w-[130px] w-[90px] md:h-[120px] h-[80px] bg-white flex flex-col items-center md:gap-4 md:p-5 p-3 md:text-[16px] font-semibold text-[12px] rounded-md text-gray-500 shadow-md hover:scale-105 hover:shadow-xl cursor-pointer transition-all'
              onClick={() => filter(item.name)}
            >
              {item.icon}
              {item.name}
            </div>
          ))}
        </div>
      )}

      <div className="px-2 sm:px-4">
        <div className="relative w-full max-h-[450px] rounded-xl shadow-lg my-6 overflow-hidden">
          <img
            src={banner}
            alt="Banner"
            className="w-full h-[250px] sm:h-[350px] md:h-[450px] object-cover"
          />
          <div className="absolute inset-0 flex flex-col justify-center pl-6 sm:pl-10 md:pl-24 pr-4 gap-4 text-white">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold leading-tight">
              Order your favorite<br />food here
            </h1>
            <p className="text-sm sm:text-base md:text-lg max-w-[500px] mt-4">
              Choose from a diverse menu featuring a delectable array of dishes crafted with the finest ingredients and satisfy your cravings and elevate your dining experience, one delicious meal at a time.
            </p>
            <button
              onClick={handleViewMenuClick}
              className="w-fit px-8 py-3 bg-white text-black font-semibold rounded-full hover:bg-orange-400 hover:text-white transition mt-4"
            >
              View Menu
            </button>
          </div>
        </div>
      </div>

      <div ref={menuRef} className='w-full flex flex-wrap gap-8 px-5 justify-center items-center pt-8 pb-8'>
        {cate.length > 0 ? (
          cate.map((item) => (
            <Card
              key={item.id}
              name={item.food_name}
              image={item.food_image}
              price={item.price}
              id={item.id}
              type={item.food_type}
            />
          ))
        ) : (
          <div className='text-gray-500 text-xl font-semibold'>No Dish Found</div>
        )}
      </div>

      <div className={`cart-sidebar md:w-[40vw] w-full h-[100vh] fixed top-0 right-0 bg-white shadow-lg p-7 transition-all duration-500 overflow-auto ${showCart ? "translate-x-0" : "translate-x-full"}`}>
      <header className='w-full flex justify-between items-center'>
          <span className='text-orange-500 font-semibold text-[24px]'>Order Items ..</span>
          <IoMdCloseCircleOutline
            className='w-[30px] h-[30px] text-orange-400 cursor-pointer hover:text-orange-600'
            onClick={() => setShowCart(false)}
          />
        </header>

        {items.length > 0 ? (
          <div>
            <div className='w-full mt-9 flex flex-col gap-6'>
              {items.map((item) => (
                <Cart
                  key={item.id}
                  name={item.name}
                  price={item.price}
                  image={item.image}
                  id={item.id}
                  qty={item.qty}
                />
              ))}
            </div>

            <div className='w-full border-t-2 border-gray-400 mt-7 flex flex-col gap-3 p-8 border-b-2'>
              <div className='flex w-full justify-between items-center'>
                <span className='text-orange-500 font-semibold text-[18px]'>Subtotal:</span>
                <span className='text-gray-500 font-semibold text-[18px]'>Rs {subtotal}/-</span>
              </div>
              <div className='flex w-full justify-between items-center'>
                <span className='text-orange-500 font-semibold text-[18px]'>Delivery Fee:</span>
                <span className='text-gray-500 font-semibold text-[18px]'>Rs {deliveryFee}/-</span>
              </div>
              <div className='flex w-full justify-between items-center'>
                <span className='text-orange-500 font-semibold text-[18px]'>Taxes:</span>
                <span className='text-gray-500 font-semibold text-[18px]'>Rs {taxes}/-</span>
              </div>
            </div>

            <div className='flex w-full justify-between items-center mt-3'>
              <span className='text-red-600 font-semibold text-[20px]'>Total:</span>
              <span className='text-gray-600 font-semibold text-[20px]'>Rs {total}/-</span>
            </div>

            {orderStep === 'button' && (
              <button
                className='w-full h-[50px] text-lg bg-orange-400 rounded-md mt-4 text-gray-800 hover:bg-orange-500 hover:text-white transition-all'
                onClick={() => setOrderStep('form')}
              >
                Place Order
              </button>
            )}

            {orderStep === 'form' && (
              <form onSubmit={handleFormSubmit} className="space-y-4 mt-6">
  <div>
    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
    <input
      type="text"
      id="name"
      name="name"
      value={form.name}
      onChange={handleChange}
      placeholder="Enter your full name"
      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-orange-400 focus:border-orange-400"
      required
    />
  </div>

  <div>
    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
    <input
      type="tel"
      id="phone"
      name="phone"
      value={form.phone}
      onChange={handleChange}
      placeholder="Enter your phone number"
      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-orange-400 focus:border-orange-400"
      required
    />
  </div>

  <div>
    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
    <input
      type="email"
      id="email"
      name="email"
      value={form.email}
      onChange={handleChange}
      placeholder="Enter your email address"
      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-orange-400 focus:border-orange-400"
      required
    />
  </div>

  <div>
    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Delivery Address</label>
    <textarea
      id="address"
      name="address"
      value={form.address}
      onChange={handleChange}
      placeholder="Enter your delivery address"
      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-orange-400 focus:border-orange-400"
      rows={4}
      required
    />
  </div>
  {orderStep === 'payment' && (
  <div className="text-center mt-6 p-6 border rounded-lg bg-white shadow-md max-w-md mx-auto">
    <h2 className="text-2xl font-semibold text-gray-800 mb-6">💳 Choose Payment Method</h2>

    <div className="space-y-4">
      <button
        onClick={() => { setPaymentMethod('cod'); handleFinalPayment(); }}
        className="w-full py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors"
      >
        Cash on Delivery
      </button>

      <button
        onClick={() => setPaymentMethod('upi')}
        className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
      >
        Pay via UPI
      </button>

      <button
        onClick={() => setPaymentMethod('card')}
        className="w-full py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium transition-colors"
      >
        Pay via Card
      </button>
    </div>

    {paymentMethod === 'upi' && (
      <div className="mt-6">
        <input
          value={upiId}
          onChange={(e) => setUpiId(e.target.value)}
          placeholder="Enter your UPI ID"
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
        />
        <button
          onClick={handleFinalPayment}
          className="w-full py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors"
        >
          Pay Now
        </button>
      </div>
    )}

    {paymentMethod === 'card' && (
      <div className="mt-6 space-y-4 text-left">
        <input
          type="text"
          maxLength={19}
          value={card.number}
          onChange={(e) => setCard({ ...card, number: e.target.value })}
          placeholder="Card Number (xxxx xxxx xxxx xxxx)"
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={card.name}
          onChange={(e) => setCard({ ...card, name: e.target.value })}
          placeholder="Name on Card"
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex gap-4">
          <input
            type="text"
            maxLength={5}
            value={card.expiry}
            onChange={(e) => setCard({ ...card, expiry: e.target.value })}
            placeholder="Expiry (MM/YY)"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            maxLength={4}
            value={card.cvv}
            onChange={(e) => setCard({ ...card, cvv: e.target.value })}
            placeholder="CVV"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={handleFinalPayment}
          className="w-full py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors"
        >
          Pay Now
        </button>
      </div>
    )}
  </div>
)}


  <button
    type="submit"
    className="text-lg bg-orange-500 text-white hover:bg-orange-600 transition-all px-5 py-3 rounded-md w-full font-semibold shadow-md"
  >
    Proceed to Payment
  </button>
</form>

            )}

            {orderStep === 'payment' && (
              <div className="text-center mt-4 p-4 border rounded bg-gray-50">
                <h2 className="text-lg font-semibold mb-2">💳 Choose Payment Method</h2>
                <div className="space-y-3">
                  <button onClick={() => { setPaymentMethod('cod'); handleFinalPayment(); }} className="bg-green-500 text-white py-2 w-full rounded">Cash on Delivery</button>
                  <button onClick={() => setPaymentMethod('upi')} className="bg-blue-500 text-white py-2 w-full rounded">Pay via UPI</button>
                  <button onClick={() => setPaymentMethod('card')} className="bg-purple-500 text-white py-2 w-full rounded">Pay via Card</button>

                  {paymentMethod === 'upi' && (
                    <div className="mt-3">
                      <input value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="Enter UPI ID" className="w-full p-2 border rounded mb-2" />
                      <button onClick={handleFinalPayment} className="bg-green-600 text-white py-2 w-full rounded">Pay Now</button>
                    </div>
                  )}

                  {paymentMethod === 'card' && (
                    <div className="mt-3 space-y-3 text-left">
                      <input type="text" maxLength={19} value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="Card Number (xxxx xxxx xxxx xxxx)" className="w-full p-2 border rounded" />
                      <input type="text" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} placeholder="Name on Card" className="w-full p-2 border rounded" />
                      <div className="flex gap-3">
                        <input type="text" maxLength={5} value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} placeholder="Expiry (MM/YY)" className="w-full p-2 border rounded" />
                        <input type="password" maxLength={4} value={card.cvv} onChange={(e) => setCard({ ...card, cvv: e.target.value })} placeholder="CVV" className="w-full p-2 border rounded" />
                      </div>
                      <button onClick={handleFinalPayment} className="bg-green-600 text-white py-2 w-full rounded hover:bg-green-700 transition">Pay Now</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {orderStep === 'success' && (
              <div className="text-center mt-4">
                <h2 className="text-2xl text-green-600 font-bold">🎉 Order Confirmed!</h2>
                <p>Your food will be delivered soon. Thank you for ordering!</p>
              </div>
            )}
          </div>
        ) : (
          <div className='flex flex-col justify-center items-center'>
            <div className='text-2xl text-center text-orange-500 font-semibold mt-5'>Empty Cart !!</div>
            <span className='text-lg text-orange-300 font-semibold mt-3'>Fill cart to enjoy your food 😋</span>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default Home;